import { Tooltip, TooltipProps } from 'components';
import { useEditableContextOptional, useScrollContextOptional } from './OptimizedTableProvider';

/**
 * TableTooltip - A Tooltip wrapper that respects table edit mode and scroll state
 *
 * Renders children without a tooltip when:
 * - Not inside a TableProvider (context is null)
 * - Any cell is being edited (editableState.rowIndex != null)
 * - Table is being scrolled (isScrolling is true)
 * - No title is provided
 *
 * Uses the split context hooks so it only re-renders when editable or scroll
 * state actually changes – not on every table instance update.
 */
const isLongTooltipTitle = (title: TooltipProps['title']): boolean => {
  if (typeof title === 'string') return title.length > 40;
  return false;
};

export const TableTooltip = ({ children, title, ...props }: TooltipProps) => {
  const editableCtx = useEditableContextOptional();
  const scrollCtx = useScrollContextOptional();

  const isAnyCellEditing = editableCtx?.editableState?.rowIndex != null;
  const isScrolling = scrollCtx?.isScrolling ?? false;
  const wrapLongText = isLongTooltipTitle(title);

  // Don't render tooltip if not inside table, editing, scrolling, or no title
  if (editableCtx === null || isAnyCellEditing || isScrolling || !title) {
    return children;
  }

  return (
    <Tooltip
      {...props}
      title={title}
      wrapLongText={wrapLongText}
      disableInteractive={wrapLongText || props.disableInteractive}
    >
      {children}
    </Tooltip>
  );
};
